"use client";

import { useDeferredValue, useState } from "react";
import {
  Checkbox,
  CopyButton,
  Notice,
  Panel,
  PillGroup,
  SelectField,
  StatCard,
  StatGrid,
  TextAreaField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  BUTTON_SMALL,
  LABEL,
  TEXTAREA,
  WIDGET_H2,
} from "@/components/tool-kit/styles";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { downloadText } from "@/lib/tool-kit/download";
import {
  DEFAULT_MINIFY_OPTIONS,
  DEFAULT_SQL_OPTIONS,
  formatSql,
  type KeywordCase,
  minifySql,
  type SqlDialect,
  type SqlFormatOptions,
  type SqlIndent,
  type SqlMode,
  type SqlResult,
  type SqlWarning,
} from "@/lib/tool-kit/latex-dev/sql-format";

/** English strings. A translated content file overrides these through `ui`. */
export const SQL_FORMATTER_STRINGS = {
  modeLabel: "Action",
  modes: {
    beautify: "Format",
    minify: "Minify",
  },
  inputLabel: "SQL to format",
  inputLabelMinify: "SQL to minify",
  placeholder: "select id, name from users where active = 1 order by name;",
  hint: "Formatted in your browser as you type. Nothing is uploaded, and the SQL is never run.",
  loadExample: "Load the example",
  clear: "Clear",
  optionsHeading: "Options",
  dialectLabel: "SQL dialect",
  dialects: {
    standard: "Standard SQL",
    postgresql: "PostgreSQL",
    mysql: "MySQL / MariaDB",
    sqlserver: "SQL Server (T-SQL)",
    sqlite: "SQLite",
  },
  dialectNotes: {
    standard:
      "Standard SQL: single quotes for text, double quotes for identifiers, -- and /* */ comments.",
    postgresql:
      "PostgreSQL: dollar-quoted strings, nested block comments, :: casts and the JSON operators ->, ->> and @> are kept whole.",
    mysql:
      "MySQL: backtick identifiers, # comments and backslash escapes in strings are kept whole.",
    sqlserver:
      "SQL Server: [bracketed] identifiers, TOP, WITH (NOLOCK) hints and GO separators are understood. Procedural T-SQL blocks are not restructured.",
    sqlite:
      "SQLite: [bracketed] and backtick identifiers and INSERT OR REPLACE are understood.",
  },
  caseLabel: "Keywords",
  cases: {
    upper: "UPPERCASE",
    lower: "lowercase",
    preserve: "As typed",
  },
  indentLabel: "Indentation",
  indents: {
    "2": "2 spaces",
    "4": "4 spaces",
    tab: "Tab",
  },
  widthLabel: "Break brackets longer than",
  widths: {
    "60": "60 characters",
    "80": "80 characters",
    "100": "100 characters",
    "120": "120 characters",
  },
  commaFirst: "Put commas at the start of the line",
  blankLines: "Leave a blank line between statements",
  keepComments:
    "Keep comments (line comments become block comments). Hints and versioned comments are always kept.",
  results: "Result",
  outputLabel: "Formatted SQL",
  outputLabelMinify: "Minified SQL",
  copyResult: "Copy SQL",
  copied: "Copied!",
  download: "Download .sql",
  filename: "query.sql",
  emptyPrompt: "Paste some SQL, or load the example, to format it.",
  statements: "Statements",
  lines: "Lines",
  charsBefore: "Characters in",
  charsAfter: "Characters out",
  sizeChange: "Change in size",
  checkHeading: "Check the result",
  allClear: "Nothing needed flagging.",
  warnings: {
    brackets:
      "The brackets do not balance, so the layout after the missing one may be off. Check for a stray or missing parenthesis.",
    string:
      "A quoted string or identifier is never closed; everything after it was treated as part of it.",
    comment: "A /* comment is never closed; everything after it is inside it.",
  },
  example:
    "select u.id, u.name, count(o.id) as orders, sum(o.total) as revenue from users u left join orders o on o.user_id = u.id and o.status <> 'cancelled' where u.created_at >= '2024-01-01' and u.country in ('US', 'CA') group by u.id, u.name having count(o.id) > 2 order by revenue desc limit 10;",
} as const;

type Strings = typeof SQL_FORMATTER_STRINGS;

const DIALECTS: readonly SqlDialect[] = [
  "standard",
  "postgresql",
  "mysql",
  "sqlserver",
  "sqlite",
];
const CASES: readonly KeywordCase[] = ["upper", "lower", "preserve"];
const INDENTS: readonly SqlIndent[] = ["2", "4", "tab"];
const WIDTHS = ["60", "80", "100", "120"] as const;

interface Settings {
  options: SqlFormatOptions;
  keepComments: boolean;
}

function run(mode: SqlMode, source: string, settings: Settings): SqlResult {
  if (mode === "minify") {
    return minifySql(source, {
      ...DEFAULT_MINIFY_OPTIONS,
      dialect: settings.options.dialect,
      keepComments: settings.keepComments,
    });
  }
  return formatSql(source, settings.options);
}

export default function SqlFormatterWidget() {
  const strings = useToolUi(SQL_FORMATTER_STRINGS);
  const fmt = useToolNumber();
  const [mode, setMode] = useState<SqlMode>("beautify");
  const [source, setSource] = useState("");
  const [settings, setSettings] = useState<Settings>({
    options: DEFAULT_SQL_OPTIONS,
    keepComments: DEFAULT_MINIFY_OPTIONS.keepComments,
  });
  const deferred = useDeferredValue(source);
  const result = deferred.trim() === "" ? null : run(mode, deferred, settings);

  return (
    <section className="space-y-6">
      <PillGroup
        label={strings.modeLabel}
        name="sqlf-mode"
        onChange={setMode}
        options={[
          { value: "beautify", label: strings.modes.beautify },
          { value: "minify", label: strings.modes.minify },
        ]}
        value={mode}
      />
      <TextAreaField
        hint={strings.hint}
        id="sqlf-input"
        label={
          mode === "beautify" ? strings.inputLabel : strings.inputLabelMinify
        }
        onChange={setSource}
        placeholder={strings.placeholder}
        rows={10}
        value={source}
      />
      <div className="flex flex-wrap gap-3">
        <button
          className={BUTTON_SMALL}
          onClick={() => setSource(strings.example)}
          type="button"
        >
          {strings.loadExample}
        </button>
        <button
          className={BUTTON_SMALL}
          onClick={() => setSource("")}
          type="button"
        >
          {strings.clear}
        </button>
      </div>
      <OptionsPanel
        mode={mode}
        onChange={setSettings}
        settings={settings}
        strings={strings}
      />
      <div aria-live="polite" className="space-y-4">
        {result === null ? (
          <Notice>{strings.emptyPrompt}</Notice>
        ) : (
          <Results
            fmt={fmt}
            mode={mode}
            result={result}
            source={deferred}
            strings={strings}
          />
        )}
      </div>
    </section>
  );
}

function OptionsPanel({
  mode,
  settings,
  onChange,
  strings,
}: {
  mode: SqlMode;
  settings: Settings;
  onChange: (next: Settings) => void;
  strings: Strings;
}) {
  const { options } = settings;
  const set = <K extends keyof SqlFormatOptions>(
    key: K,
    value: SqlFormatOptions[K]
  ) => onChange({ ...settings, options: { ...options, [key]: value } });
  return (
    <Panel title={strings.optionsHeading}>
      <SelectField
        hint={strings.dialectNotes[options.dialect]}
        id="sqlf-dialect"
        label={strings.dialectLabel}
        onChange={(value) => set("dialect", value)}
        options={DIALECTS.map((value) => ({
          value,
          label: strings.dialects[value],
        }))}
        value={options.dialect}
      />
      {mode === "beautify" ? (
        <BeautifyOptions options={options} set={set} strings={strings} />
      ) : (
        <Checkbox
          checked={settings.keepComments}
          label={strings.keepComments}
          onChange={(value) => onChange({ ...settings, keepComments: value })}
        />
      )}
    </Panel>
  );
}

function BeautifyOptions({
  options,
  set,
  strings,
}: {
  options: SqlFormatOptions;
  set: <K extends keyof SqlFormatOptions>(
    key: K,
    value: SqlFormatOptions[K]
  ) => void;
  strings: Strings;
}) {
  return (
    <>
      <div className="grid gap-4 sm:grid-cols-3">
        <SelectField
          id="sqlf-case"
          label={strings.caseLabel}
          onChange={(value) => set("keywordCase", value)}
          options={CASES.map((value) => ({
            value,
            label: strings.cases[value],
          }))}
          value={options.keywordCase}
        />
        <SelectField
          id="sqlf-indent"
          label={strings.indentLabel}
          onChange={(value) => set("indent", value)}
          options={INDENTS.map((value) => ({
            value,
            label: strings.indents[value],
          }))}
          value={options.indent}
        />
        <SelectField
          id="sqlf-width"
          label={strings.widthLabel}
          onChange={(value) => set("maxWidth", Number(value))}
          options={WIDTHS.map((value) => ({
            value,
            label: strings.widths[value],
          }))}
          value={String(options.maxWidth) as (typeof WIDTHS)[number]}
        />
      </div>
      <div className="flex flex-col gap-2">
        <Checkbox
          checked={options.commaFirst}
          label={strings.commaFirst}
          onChange={(value) => set("commaFirst", value)}
        />
        <Checkbox
          checked={options.blankLines}
          label={strings.blankLines}
          onChange={(value) => set("blankLines", value)}
        />
      </div>
    </>
  );
}

function countLines(text: string): number {
  return text === "" ? 0 : text.split("\n").length;
}

function Results({
  result,
  source,
  mode,
  strings,
  fmt,
}: {
  result: SqlResult;
  source: string;
  mode: SqlMode;
  strings: Strings;
  fmt: ReturnType<typeof useToolNumber>;
}) {
  const before = source.length;
  const after = result.text.length;
  const change = before === 0 ? 0 : ((after - before) / before) * 100;
  return (
    <>
      <h2 className={WIDGET_H2}>{strings.results}</h2>
      <StatGrid>
        <StatCard
          emphasis
          label={strings.statements}
          value={fmt(result.statements)}
        />
        <StatCard label={strings.lines} value={fmt(countLines(result.text))} />
        <StatCard label={strings.charsBefore} value={fmt(before)} />
        <StatCard label={strings.charsAfter} value={fmt(after)} />
        <StatCard
          label={strings.sizeChange}
          value={`${change > 0 ? "+" : ""}${fmt(change, { maximumFractionDigits: 1 })}%`}
        />
      </StatGrid>
      <div className="space-y-2">
        <label className={LABEL} htmlFor="sqlf-output">
          {mode === "beautify"
            ? strings.outputLabel
            : strings.outputLabelMinify}
        </label>
        <textarea
          className={TEXTAREA}
          id="sqlf-output"
          readOnly
          rows={Math.min(24, countLines(result.text) + 1)}
          spellCheck={false}
          value={result.text}
        />
      </div>
      <div className="flex flex-wrap gap-3">
        <CopyButton
          copiedLabel={strings.copied}
          label={strings.copyResult}
          text={result.text}
        />
        <button
          className={BUTTON_SECONDARY}
          onClick={() =>
            downloadText(
              strings.filename,
              `${result.text}\n`,
              "application/sql"
            )
          }
          type="button"
        >
          {strings.download}
        </button>
      </div>
      <Warnings strings={strings} warnings={result.warnings} />
    </>
  );
}

function Warnings({
  warnings,
  strings,
}: {
  warnings: SqlWarning[];
  strings: Strings;
}) {
  if (warnings.length === 0) {
    return <Notice>{strings.allClear}</Notice>;
  }
  return (
    <ul className="list-disc space-y-1 pl-5 text-foreground text-sm">
      {warnings.map((warning) => (
        <li key={warning}>{strings.warnings[warning]}</li>
      ))}
    </ul>
  );
}
